import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { api, type Paginated } from '@/lib/api';

interface Producer { id: number; name: string; country: string | null; wines_count?: number }
interface Enologist { id: number; name: string; country: string | null; wines_count?: number }
interface Place { id: number; name: string; type_label: string; display_path?: string }
interface Supplier { id: number; name: string; type_label: string }
interface PriceSource { id: number; name: string; type: string; is_global: boolean }

const tabs = ['Productores', 'Enólogos', 'Lugares', 'Proveedores', 'Fuentes precio'] as const;

export function CatalogPage() {
  const [tab, setTab] = useState<typeof tabs[number]>('Productores');

  return (
    <div className="p-8">
      <header className="mb-6">
        <h1 className="text-2xl font-medium">Catálogos</h1>
        <p className="text-sm text-notion-500">Productores, enólogos, lugares, proveedores y fuentes de precio.</p>
      </header>

      <div className="flex gap-2 mb-4 border-b border-notion-200 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ' +
              (tab === t ? 'border-notion-800 text-notion-800' : 'border-transparent text-notion-500 hover:text-notion-800')
            }
          >{t}</button>
        ))}
      </div>

      {tab === 'Productores' && <ProducersList />}
      {tab === 'Enólogos' && <EnologistsList />}
      {tab === 'Lugares' && <PlacesList />}
      {tab === 'Proveedores' && <SuppliersList />}
      {tab === 'Fuentes precio' && <PriceSourcesList />}
    </div>
  );
}

function ProducersList() {
  const { data, isLoading } = useQuery({
    queryKey: ['producers'],
    queryFn: async () => (await api.get<Paginated<Producer>>('/producers', { params: { per_page: 200 } })).data.data,
  });
  return <SimpleTable headers={['Nombre', 'País', 'Vinos']} rows={data?.map((p) => [p.name, p.country ?? '—', p.wines_count ?? 0])} loading={isLoading} />;
}

function EnologistsList() {
  const { data, isLoading } = useQuery({
    queryKey: ['enologists'],
    queryFn: async () => (await api.get<Paginated<Enologist>>('/enologists', { params: { per_page: 200 } })).data.data,
  });
  return <SimpleTable headers={['Nombre', 'País', 'Vinos']} rows={data?.map((p) => [p.name, p.country ?? '—', p.wines_count ?? 0])} loading={isLoading} />;
}

function PlacesList() {
  const { data, isLoading } = useQuery({
    queryKey: ['places'],
    queryFn: async () => (await api.get<Paginated<Place>>('/places', { params: { per_page: 500 } })).data.data,
  });
  return <SimpleTable headers={['Tipo', 'Nombre', 'Camino']} rows={data?.map((p) => [p.type_label, p.name, p.display_path ?? p.name])} loading={isLoading} />;
}

function SuppliersList() {
  const { data, isLoading } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => (await api.get<Paginated<Supplier>>('/suppliers', { params: { per_page: 200 } })).data.data,
  });
  return <SimpleTable headers={['Nombre', 'Tipo']} rows={data?.map((p) => [p.name, p.type_label])} loading={isLoading} />;
}

function PriceSourcesList() {
  const { data, isLoading } = useQuery({
    queryKey: ['price-sources'],
    queryFn: async () => (await api.get<Paginated<PriceSource>>('/price-sources', { params: { per_page: 200 } })).data.data,
  });
  return (
    <SimpleTable
      headers={['Nombre', 'Tipo', 'Alcance']}
      rows={data?.map((p) => [p.name, p.type, p.is_global ? 'Global' : 'Mi cava'])}
      loading={isLoading}
    />
  );
}

function SimpleTable({ headers, rows, loading }: { headers: string[]; rows?: any[][]; loading?: boolean }) {
  return (
    <div className="card overflow-hidden">
      {loading ? (
        <div className="p-12 text-center text-notion-500">Cargando…</div>
      ) : !rows?.length ? (
        <div className="p-12 text-center text-notion-500">Sin datos.</div>
      ) : (
        <table className="table-base">
          <thead className="bg-white">
            <tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-white">
                {row.map((cell: any, j: number) => <td key={j}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
